import clsx from 'clsx';
import { forwardRef, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { CollapsibleButton } from './collapsible-button';
import { CollapsibleContent } from './collapsible-content/collapsible-content';
import { CollapsibleProvider } from './collapsible-provider';
import classes from './collapsible.module.scss';

export type CollapsibleProps = PropsWithChildren<{
  /**
   * The `title` variable can hold either a ReactNode or a function that returns a ReactNode.
   */
  title: ReactNode | (({ open }: { open: boolean; toggle: () => void }) => ReactNode);

  /**
   * Represents an optional callback function for onChange event.
   * The onChange function gets invoked whenever the value of an open parameter changes.
   */
  onChange?: (open: boolean) => void;

  /**
   * Initial state
   */
  open?: boolean;
}>;

export const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(
  ({ title, open = false, onChange, children }, ref) => {
    const [isOpen, setIsOpen] = useState(open);

    useEffect(() => {
      setIsOpen(open);
    }, [open]);

    const onToggle = () => {
      setIsOpen(!isOpen);
      onChange?.(!isOpen);
    };

    return (
      <CollapsibleProvider isOpen={isOpen} onToggle={onToggle}>
        <div ref={ref} className={clsx(classes.root, 'collapsible-component', { [classes.isOpen]: isOpen })}>
          <CollapsibleButton>
            <div className={classes.toggle}>
              {typeof title === 'function' ? title({ open: isOpen, toggle: onToggle }) : title}
            </div>
          </CollapsibleButton>
          <CollapsibleContent
            className={clsx(classes.content, { [classes.closed]: !isOpen })}
          >
            {children}
          </CollapsibleContent>
        </div>
      </CollapsibleProvider>
    );
  },
);
